import type { Extend } from '@mentimeter/ragnar-dsc';
import { Box } from '@mentimeter/ragnar-ui/box';
import { FlexibleText } from '@mentimeter/slides-presentation-ui/text/flexible-text';
import { MarkdownText } from '@mentimeter/slides-presentation-ui/text/markdown-text';
import type { ParagraphStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getNinePointLayoutProperties } from '../../shared/utils/getNinePointLayoutProperties';
import { ContentSlideTitleSection } from '../../shared/components/ContentSlideTitleSection';
import { StaggerCss } from '../../shared/components/StaggerCss';
import { getLegacyLayout } from '../../utils/legacyContentSlideUtils/getLegacyLayout';

export function ParagraphPresentation() {
  const {
    Utils: { fontOffset },
    useConfig,
  } = usePresentationContext();

  const slide = usePresentationSlideContext();
  const staticContent = slide.staticContent as ParagraphStaticContent;
  const fontSizeOffset = staticContent.textSizeOffset;
  const title = slide.title || staticContent.heading;
  const legacyLayout = getLegacyLayout(slide) ?? {
    type: 'default',
    font_size_offset: 0,
    alignment_x: 1,
    alignment_y: 1,
    size: '25%',
  };

  const { enableAnimations } = useConfig();

  const { textAlign, justifyContent, alignItems } =
    getNinePointLayoutProperties(legacyLayout);

  const breakpoints = [
    { count: 500, offset: -3 },
    { count: 400, offset: -2 },
    { count: 300, offset: -1 },
  ];

  const EXTEND: Extend = () => ({ whiteSpace: 'pre-line' });

  const fullText = staticContent.paragraph
    ? staticContent.paragraph.length + title.length
    : title.length;
  const width = 30;
  const height = Math.ceil(fullText / width);
  const offset =
    fontOffset({
      height,
      width,
      breakpoints,
    }) + (fontSizeOffset ?? 0);

  return (
    <Box
      width="100%"
      height="70%"
      data-testid="paragraph-container"
      alignItems={alignItems}
      justifyContent={justifyContent}
    >
      <ContentSlideTitleSection
        question={title}
        title_meta={staticContent.label}
        layout={legacyLayout}
        textAlign={textAlign}
        alignItems={alignItems}
        enableAnimations={true}
        lineHeight="none"
        justifyContent="flex-end"
      />
      {staticContent.paragraph && (
        <Box maxWidth="48em" mt={3}>
          <FlexibleText
            mt={3}
            mb={0}
            textAlign={textAlign}
            fontOffset={offset - 1}
            fontSize={8}
            color="text"
            extend={EXTEND}
          >
            <StaggerCss
              index={2}
              enableAnimations={enableAnimations}
              type="brand"
            >
              <MarkdownText
                source={staticContent.paragraph}
                disableLineBreaks={false}
                allowBlankLines
                allowHyperlinks
              />
            </StaggerCss>
          </FlexibleText>
        </Box>
      )}
    </Box>
  );
}
